/*
 * File: c:\Users\j.mazat\Desktop\Autobuy\AutobuyBackOffice\src\Content\User\UserSingle.tsx
 * Project: c:\Users\j.mazat\Desktop\Autobuy\AutobuyBackOffice
 * Created Date: Tuesday July 18th 2023
 * Author: Jonathan Mazat
 * -----
 * Last Modified: Tuesday, 16th January 2024 12:36:57
 * Modified By: Simon (s.gourlet@autobuy.fr>)
 * -----
 * Copyright (c) 2023 Autobuy SAS
 * -----
 */

import { Fragment, useRef, useState } from "react";
import PhoneInput from "react-phone-number-input";
import { UpdateUserIntel, UpdateUserType, UserSingleType, UserStatus } from "Type/user.type";
import { useLoaderData, useNavigate } from "react-router-dom";
import translator from "MultiLanguage/TranslationWrapper";
import Styles from "Content/User/UserSingle.module.css";
import TitlePage from "Content/HeadingPage/TitlePage/TitlePage";
import "react-phone-number-input/style.css";
import "Content/Contact/UserForm/PhoneInput.css";
import UserService from "Services/Users/UserService";
import ModalConfirmation from "Content/Modals/ModalConfirmation/ModalConfirmation";
import ModalSearchCompany from "Content/Modals/ModalSearchCompany/ModalSearchCompany";
import { Button, TextField } from "@mui/material";
import { FaPencilAlt } from "react-icons/fa";
import { Form } from "react-bootstrap";
import UserTabs from "./UserTabs/UserTabs";
import { UserSeenCarType } from "Type/seenCar.type";
import RouterPathResolver from "Config/Router/RouterPathResolver";
import { formatSiren } from "Utils/company.utils";

function UserSingle() {
    const navigate = useNavigate();
    const loadData = useLoaderData() as {userData: UserSingleType, usertab: number | undefined, offertab: number | undefined, seenCars: UserSeenCarType[]};
    const createdAt = new Date(loadData.userData.CreatedAt);
    const [updateEmail, setUpdateEmail] = useState(false);
    const [email, setEmail] = useState(loadData.userData.Email);
    const [firstName, setFirstName] = useState(loadData.userData.FirstName);
    const [lastName, setLastName] = useState(loadData.userData.LastName);
    const [phone, setPhone] = useState(loadData.userData.Phone);
    const [cellPhone, setCellPhone] = useState(loadData.userData.CellPhone);
    const [address, setAddress] = useState(loadData.userData.Address);
    const [postalCode, setPostalCode] = useState(loadData.userData.PostalCode);
    const [city, setCity] = useState(loadData.userData.City);
    const [state, setState] = useState(loadData.userData.State);
    const [country, setCountry] = useState(loadData.userData.Country);
    const [comments, setComments] = useState(loadData.userData.Comments);
    const [status, setStatus] = useState(loadData.userData.Status);
    const [updatable, setUpdatable] = useState(false);
    const [buttonEnable, setButtonEnable]  = useState<boolean>(true);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [openModalCompany, setOpenModalCompany] = useState<boolean>(false);
    const [company, setCompany] = useState<string>(loadData.userData.Company?.Name);
    const siren = formatSiren(loadData.userData.Company.Siren);
    const [companyID, setCompanyID] = useState<string>(loadData.userData.Company?.id);
    const [isOkay, setIsOkay] = useState<boolean>(false);
    const updateObject = useRef<UpdateUserIntel>({update: {}, add: {groups: [], parameters: []}, remove: {groups: [], parameters: []}});

    const setUpdateUserIntelObject = (val: UpdateUserType) => {
        if (val.Email && !/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(val.Email))
            val.Email = undefined;
        updateObject.current.update = {...updateObject.current.update, ...val};
        if (Object.values(updateObject.current.update).some((val) => val != undefined))
            setUpdatable(true);
        else
            setUpdatable(false);
    };

    const manageSetter = (IsOkay: boolean, OpenModal: boolean, ButtonEnable: boolean) => {
        setIsOkay(IsOkay);
        setOpenModal(OpenModal);
        setButtonEnable(ButtonEnable);
    };

    const manageUpdate = async () => {
        if ((Object.values(updateObject.current.update).some((val) => val != undefined)
            || (updateObject.current.add.groups.length > 0 || updateObject.current.remove.groups.length > 0)
            || (updateObject.current.add.parameters.length > 0 || updateObject.current.remove.parameters.length > 0))
            && window.confirm(`${translator.decode("users.save_confirm").replace("%s", JSON.stringify(updateObject.current))}`)) {
            try {
                if (Object.values(updateObject.current.update).some((val) => val != undefined)) {
                    await UserService.updateUser(loadData.userData.id, updateObject.current.update);
                    updateObject.current.update = {};
                }
                setUpdatable(false);
                manageSetter(true, true, true);
            } catch (reason: any) {
                manageSetter(false, true, true);
            }
        }
    };

    const handleResendCGV = async () => {
        setButtonEnable(false);
        UserService.resendCGV(loadData.userData.id).then((res) => {
            manageSetter(true, true, true);
        }).catch((reason) => {
            manageSetter(false, true, true);
        });
    };

    const handleCloseModalCompany = () => {
        setOpenModalCompany(false);
    };

    const handleModalClose = () => {
        manageSetter(false, false, true);
    };

    const handleResendEmailVerification = () => {
        setButtonEnable(false);
        UserService.resendEmailVerification(loadData.userData.id).then((res) => {
            manageSetter(true, true, true);
        }).catch((reason) => {
            manageSetter(false, true, true);
        });
    };

    const handleUpdateStatus = (status: UserStatus) => {
        setButtonEnable(false);
        UserService.updateStatus(loadData.userData.id, status).then((res) => {
            manageSetter(true, true, true);
            setStatus(status);
        }).catch((reason) => {
            manageSetter(false, true, true);
        });
    };

    return (
        <Fragment>
            <div className="p-3">
                <TitlePage title={translator.decode("dynamic.user")} />
                <div className={`${Styles.actionHolder}`}>
                    <span className={`${Styles.action}`}>
                        {status === "NEW" ?
                            <Fragment>
                                <button disabled={!buttonEnable} className="btn btn-outline-primary" onClick={async (e) => {e.stopPropagation(); handleResendCGV();}}>{translator.decode("users.sendCGV")}</button>
                                <button disabled={!buttonEnable} className="btn btn-outline-info" onClick={async (e) => {e.stopPropagation(); handleResendEmailVerification();}}>{translator.decode("users.sendVerificationLink")}</button>
                            </Fragment>
                            : null
                        }
                        {(status === "PENDING" || status === "ARCHIVED") ?
                            <button disabled={!buttonEnable} className="btn btn-outline-success" onClick={(e) => {e.stopPropagation(); manageUpdate(); handleUpdateStatus(UserStatus.VALID)}}>{translator.decode("dynamic.valid")}</button>
                            : null
                        }
                        {status !== "ARCHIVED" ?
                            <button disabled={!buttonEnable} className="btn btn-outline-danger" onClick={(e) => {e.stopPropagation(); handleUpdateStatus(UserStatus.ARCHIVED);}}>{translator.decode("dynamic.archive")}</button>
                            : null
                        }
                        <button disabled={!buttonEnable || !updatable} className="btn btn-outline-info" onClick={manageUpdate}>{translator.decode("dynamic.save")}</button>
                    </span>
                </div>
                <div className={`${Styles.holderUser} mb-3`}>
                    <div className={`${Styles.block}`}>
                        <h3>
                            {translator.decode("dynamic.general")}
                        </h3>
                        <div className={`${Styles.block}`}>
                            <div> {translator.decode("dynamic.createdAt")} {createdAt.toLocaleDateString()} </div>
                            {updateEmail ?
                                <Form.Group className={`${Styles.formfield}`} >
                                    <TextField label={translator.decode("dynamic.email")} sx={{width:"100%"}} size="small" required type={"email"} value={email ?? ""} onChange={(e) => {setEmail(e.target.value); setUpdateUserIntelObject({Email: e.target.value})}} />
                                </Form.Group>
                                :
                                <div className="d-inline-flex">
                                    <div style={{fontWeight:"bold"}}><p className="m-0">{translator.decode("dynamic.email")}: <span className={`${Styles.bold}`}>{loadData.userData.Email}</span></p></div>
                                    <Button onClick={(e) => setUpdateEmail(true)}>
                                        <FaPencilAlt />
                                    </Button>
                                </div>
                            }
                            <div className={`${Styles.company}`}>
                                <div style={{cursor:"pointer"}} onClick={()=> {navigate(RouterPathResolver.pathCompany_BY_ID.path.replace(":id", companyID))}}> {translator.decode("dynamic.company")}: {company.toUpperCase()} </div>
                                <Button onClick={() => setOpenModalCompany(true)}>
                                    <FaPencilAlt />
                                </Button>
                            </div>
                            <div>{translator.decode("dynamic.siren")}: {siren}</div>
                        </div>
                        <div className={`${Styles.status}`}>
                            {translator.decode("dynamic.status")}: {translator.decode(`dynamic.${loadData.userData.Status}`)}
                            {status === "NEW" ? <span className="text-danger"> {translator.decode("dynamic.newAccount")} </span> : null}
                        </div>
                        <div className={`${Styles.status}`}>
                            <span>{translator.decode("users.lastConnexion")}:</span> {(loadData.userData.ConnexionList && loadData.userData.ConnexionList.length > 0 ? new Date(loadData.userData.ConnexionList.sort((val1, val2) => {return (new Date(val1.Date)).getTime() - (new Date(val2.Date)).getTime()}).reverse()[0].Date): "").toLocaleString()}
                        </div>
                    </div>
                    <div className={`${Styles.block}`}>
                        <h3>
                            {translator.decode("dynamic.information")}
                        </h3>
                        <div className={`${Styles.personalInfo}`}>
                            <div>
                                <p>{translator.decode("dynamic.lastname")}:</p>
                                <input type="text" value={lastName} onChange={(e) => {setLastName(e.target.value); setUpdateUserIntelObject({LastName: e.target.value});}}/>
                            </div>
                            <div>
                                <p>{translator.decode("dynamic.firstname")}:</p>
                                <input type="text" value={firstName} onChange={(e) => {setFirstName(e.target.value); setUpdateUserIntelObject({FirstName: e.target.value});}}/>
                            </div>
                            <div>
                                <p>{translator.decode("dynamic.phone")}:</p>
                                <PhoneInput defaultCountry="FR" autoComplete="on" value={phone} onChange={(e: any) => {setPhone(e); setUpdateUserIntelObject({Phone: e})}}  />
                            </div>
                            <div>
                                <p>{translator.decode("dynamic.cellPhone")}:</p>
                                <PhoneInput defaultCountry="FR" autoComplete="on" value={cellPhone} onChange={(e: any) => {setCellPhone(e); setUpdateUserIntelObject({CellPhone: e})}} />
                            </div>
                        </div>
                    </div>
                    <div className={`${Styles.block}`}>
                        <h3>
                            {translator.decode("dynamic.address")}
                        </h3>
                        <div className={`${Styles.address}`}>
                            <div><p>{translator.decode("dynamic.street")}:</p> <input type="text" value={address} onChange={(e) => {setAddress(e.target.value); setUpdateUserIntelObject({Address: e.target.value})}}/></div>
                            <div><p>{translator.decode("dynamic.postalcode")}:</p> <input type="text" value={postalCode} onChange={(e) => {setPostalCode(e.target.value); setUpdateUserIntelObject({PostalCode: e.target.value})}}/></div>
                            <div><p>{translator.decode("dynamic.city")}:</p> <input type="text" value={city} onChange={(e) => {setCity(e.target.value); setUpdateUserIntelObject({City: e.target.value})}}/></div>
                            <div><p>{translator.decode("dynamic.state")}:</p> <input type="text" value={state ?? "N/C"} onChange={(e) => {setState(e.target.value); setUpdateUserIntelObject({State: e.target.value})}}/></div>
                            <div><p>{translator.decode("dynamic.country")}:</p> <input type="text" value={country} onChange={(e) => {setCountry(e.target.value); setUpdateUserIntelObject({Country: e.target.value})}}/></div>
                        </div>
                    </div>
                </div>
                <div className="m-3">
                    <p>{translator.decode("dynamic.comment")}:</p>
                    <textarea className={`w-100`} maxLength={1024} value={comments} onChange={(e) => {setComments(e.target.value); updateObject.current.update.Comments = e.target.value; setUpdateUserIntelObject({Comments: e.target.value})}} />
                </div>
            </div>
            <hr />
            <UserTabs userID={loadData.userData.id} offers={[]} SeenCar={loadData.seenCars} usertab={loadData.usertab} offertab={undefined} />
            <ModalSearchCompany setCompanyID={setCompanyID} setCompany={setCompany} ClientID={loadData.userData.id} show={openModalCompany} onClose={handleCloseModalCompany} closeModal={handleCloseModalCompany} title={""} submitFunc={UserService.updateUser} />
            <ModalConfirmation show={openModal} onClose={handleModalClose} closeModal={handleModalClose} isOkay={isOkay} />
        </Fragment>
    )
}

export default UserSingle;
